import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Message } from 'semantic-ui-react';
import { OrganizationRecordRead, OrganizationRecordCreateUpdate } from 'store/generated/admin/organization-records';
import { organizationRecordApi } from '../api/organizationRecordApi';

interface OrganizationRecordDataModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  recordValueId: number;
  recordValueName: string;
  assessmentPeriodId?: number;
  assessmentPeriodName?: string;
  existingRecord?: OrganizationRecordRead | null;
}

const OrganizationRecordDataModal: React.FC<OrganizationRecordDataModalProps> = ({
  open,
  onClose,
  organizationId,
  recordValueId,
  recordValueName,
  assessmentPeriodId,
  assessmentPeriodName,
  existingRecord,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createRecord] = organizationRecordApi.endpoints.apiAdminV1OrganizationRecordsCreate.useMutation();
  const [updateRecord] = organizationRecordApi.endpoints.apiAdminV1OrganizationRecordsUpdate.useMutation();

  useEffect(() => {
    if (existingRecord) {
      setValue(existingRecord.value || '');
    } else {
      setValue('');
    }
    setError(null);
  }, [existingRecord, open]);

  const handleSubmit = async () => {
    if (!value.trim()) {
      setError('Value is required');
      return;
    }

    try {
      const payload: OrganizationRecordCreateUpdate = {
        organization: organizationId,
        organization_record_value: recordValueId,
        value: value.trim(),
        assessment_period: assessmentPeriodId || null,
      };

      if (existingRecord) {
        await updateRecord({
          id: existingRecord.id,
          organizationRecordCreateUpdate: payload,
        }).unwrap();
      } else {
        await createRecord({
          organizationRecordCreateUpdate: payload,
        }).unwrap();
      }

      onClose();
    } catch (err: any) {
      setError(err?.data?.detail || 'Failed to save organization record');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        {recordValueName}
        {assessmentPeriodName && ` - ${assessmentPeriodName}`}
      </Modal.Header>
      <Modal.Content>
        {error && <Message negative>{error}</Message>}
        <Form>
          <Form.Field required>
            <label>{recordValueName}</label>
            <Form.Input
              placeholder={`Enter ${recordValueName.toLowerCase()}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button primary onClick={handleSubmit}>
          {existingRecord ? 'Update' : 'Create'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default OrganizationRecordDataModal;
